import { GridSpaceDataType, PositionType } from 'data/types';
import { isPointInPolygon } from 'utils/footprint';
import { isPolygonGridSpace, polygonInteriorPoint } from 'utils/space-polygon';

export function getSpaceAbsoluteVertices(
  space: GridSpaceDataType
): PositionType[] {
  if (!space.vertices) return [];
  return space.vertices.map((v) => ({
    x: v.x + space.position.x,
    y: v.y + space.position.y
  }));
}

export function getSpacePolygonPx(space: GridSpaceDataType): PositionType[] {
  if (space.vertices && space.vertices.length >= 3) {
    return getSpaceAbsoluteVertices(space);
  }
  const { x, y, width, height } = space.position;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height }
  ];
}

function distanceFromPointToPolygon(
  point: { x: number; y: number },
  polygon: PositionType[]
): number {
  if (isPointInPolygon(point, polygon)) return 0;
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    const t =
      lenSq === 0
        ? 0
        : Math.max(
            0,
            Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq)
          );
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    const d = Math.hypot(point.x - cx, point.y - cy);
    if (d < best) best = d;
  }
  return best;
}

export function isPolygonShapedSpace(space: GridSpaceDataType): boolean {
  return isPolygonGridSpace(
    space.vertices,
    space.position.width,
    space.position.height
  );
}

export function getSpaceInteriorCenter(space: GridSpaceDataType): {
  x: number;
  y: number;
} {
  const bboxCenter = {
    x: space.position.x + space.position.width / 2,
    y: space.position.y + space.position.height / 2
  };

  if (!isPolygonShapedSpace(space)) return bboxCenter;

  const absoluteVertices = getSpaceAbsoluteVertices(space);
  if (absoluteVertices.length < 3) return bboxCenter;
  if (isPointInPolygon(bboxCenter, absoluteVertices)) return bboxCenter;

  const interior = polygonInteriorPoint(absoluteVertices);
  return interior ?? bboxCenter;
}

export function clampPointToZonePolygons(
  pointPx: { x: number; y: number },
  zoneGridSpaces: GridSpaceDataType[]
): { x: number; y: number } {
  if (zoneGridSpaces.length === 0) return pointPx;

  for (const space of zoneGridSpaces) {
    const polygon = getSpacePolygonPx(space);
    if (polygon.length >= 3 && isPointInPolygon(pointPx, polygon)) {
      return pointPx;
    }
  }

  let nearest = zoneGridSpaces[0];
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const space of zoneGridSpaces) {
    const polygon = getSpacePolygonPx(space);
    if (polygon.length >= 3) {
      const d = distanceFromPointToPolygon(pointPx, polygon);
      if (d < nearestDistance) {
        nearestDistance = d;
        nearest = space;
      }
    }
  }
  return getSpaceInteriorCenter(nearest);
}

export function clampPointToStructureFootprint(
  pointPx: { x: number; y: number },
  levelGridSpaces: GridSpaceDataType[]
): { x: number; y: number } {
  return clampPointToZonePolygons(pointPx, levelGridSpaces);
}

export function clampPointToSpacePolygon(
  pointPx: { x: number; y: number },
  space: GridSpaceDataType
): { x: number; y: number } {
  const polygon = getSpacePolygonPx(space);
  if (polygon.length < 3) {
    const { x, y, width, height } = space.position;
    return {
      x: Math.min(Math.max(pointPx.x, x), x + width),
      y: Math.min(Math.max(pointPx.y, y), y + height)
    };
  }
  if (isPointInPolygon(pointPx, polygon)) return pointPx;
  return getSpaceInteriorCenter(space);
}
